import { useMemo } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import {
    RuleActionType,
    RuleConditionType,
    TransactionRule,
    TransactionRuleAction,
    TransactionRuleGroup,
    parseAccountIdActionValue,
    parseAccrualAccountActionValue,
} from "@common/types/transactionRule";
import {
    RuleScopeContext,
    RuleWarnings,
    computeRuleGroupWarnings,
} from "@common/helpers/ruleOverrides";
import {
    useWorkspaceAccounts,
    useWorkspaceAccrualAccounts,
} from "@api/account.api";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";

interface GetNewOrderForRuleReorderParams {
    sourceGroup: TransactionRuleGroup;
    targetGroup: TransactionRuleGroup;
    sourceRuleIndex: number;
    targetRuleIndex: number;
}

export const getNewOrderForRuleReorder = ({
    sourceGroup,
    targetGroup,
    sourceRuleIndex,
    targetRuleIndex,
}: GetNewOrderForRuleReorderParams): number => {
    let result: number;

    // the rule has been moved inside the same group
    if (sourceGroup.id === targetGroup.id) {
        // Move within the same group
        const updatedRules = arrayMove(
            sourceGroup.rules ?? [],
            sourceRuleIndex,
            targetRuleIndex,
        );

        result =
            ((updatedRules[targetRuleIndex - 1]?.order ?? 0) +
                (updatedRules[targetRuleIndex + 1]?.order ??
                    updatedRules.length + 1)) /
            2;
    } else {
        // Moved to a different group
        const movedRule = sourceGroup.rules?.[sourceRuleIndex];
        if (!movedRule) {
            throw new Error("Moved rule not found");
        }
        const newTargetRules = [
            ...(targetGroup.rules?.slice(0, targetRuleIndex) ?? []),
            ...(movedRule ? [movedRule] : []),
            ...(targetGroup.rules?.slice(targetRuleIndex) ?? []),
        ];

        result =
            ((newTargetRules[targetRuleIndex - 1]?.order ?? 0) +
                (newTargetRules[targetRuleIndex + 1]?.order ??
                    newTargetRules.length + 1)) /
            2;
    }

    return result;
};

/**
 * Build a stable `RuleScopeContext` from the workspace's financial accounts
 * (for entity / account / subaccount hierarchy). The resulting context is the
 * input to the pure `computeRuleGroupWarnings` helper.
 */
export const useRuleScopeContext = (): RuleScopeContext => {
    const financialAccounts = useFinancialAccounts();

    return useMemo(() => {
        const financialAccountEntityId = new Map<number, number | null>();
        const subaccountFinancialAccountId = new Map<string, number>();
        for (const fa of financialAccounts) {
            financialAccountEntityId.set(fa.id, fa.entityId);
            for (const sub of fa.subaccounts ?? []) {
                subaccountFinancialAccountId.set(sub.id, fa.id);
            }
        }

        return {
            financialAccountEntityId,
            subaccountFinancialAccountId,
        };
    }, [financialAccounts]);
};

/**
 * Compute per-rule override warnings for every rule in a group in a single
 * pass. Returns a stable `Map` reference while inputs are structurally equal,
 * so row components can pull their flags as O(1) lookups without re-running
 * the sweep on each row render.
 *
 * Accepts a pre-built `RuleScopeContext` so the context can be lifted to the
 * table level and shared across all groups.
 */
export const useRuleGroupWarnings = (
    group: TransactionRuleGroup,
    context: RuleScopeContext,
): Map<string, RuleWarnings> =>
    useMemo(() => computeRuleGroupWarnings(group, context), [group, context]);

export const useHasDisabledAccount = (rule: TransactionRule) => {
    const { data: cashAccounts } = useWorkspaceAccounts();
    const { data: accrualAccounts } = useWorkspaceAccrualAccounts();
    const accounts = useMemo(
        () => [...(cashAccounts ?? []), ...(accrualAccounts ?? [])],
        [cashAccounts, accrualAccounts],
    );

    const accountIdActions = rule.actions.filter(
        (action) =>
            action.actionType === RuleActionType.ACCOUNT_ID ||
            action.actionType === RuleActionType.ACCRUAL_ACCOUNT_ID,
    );
    const accountNameActions = rule.actions.filter(
        (action) =>
            action.actionType === RuleActionType.ACCOUNT_NAME ||
            action.actionType === RuleActionType.ACCRUAL_ACCOUNT_NAME,
    );

    if (accountIdActions.length === 0 && accountNameActions.length === 0) {
        return false;
    }

    const hasDisabledById = accountIdActions.some((action) =>
        getAccountActionAccountIds(action).some((accountId) =>
            accounts.some(
                (account) => account.id === accountId && account.isDisabled,
            ),
        ),
    );
    if (hasDisabledById) {
        return true;
    }

    if (accountNameActions.length === 0) {
        return false;
    }

    const entityCondition = rule.conditions.find(
        (condition) => condition.conditionType === RuleConditionType.ENTITY,
    );
    if (!entityCondition) {
        return false;
    }
    const entityConditionValue = Array.isArray(entityCondition.value)
        ? entityCondition.value
        : [entityCondition.value];

    return accountNameActions.some((action) => {
        const accountName = getAccountActionAccountName(action);
        if (!accountName) {
            return false;
        }
        return accounts.some(
            (account) =>
                account.name === accountName &&
                account.isDisabled &&
                entityConditionValue.includes(account.entityId),
        );
    });
};

export function getAccountActionAccountIds(
    action: TransactionRuleAction,
): string[] {
    if (action.actionType === RuleActionType.ACCRUAL_ACCOUNT_ID) {
        return parseAccrualAccountActionValue(action.value).accountIds ?? [];
    }
    return parseAccountIdActionValue(action.value) ?? [];
}

function getAccountActionAccountName(
    action: TransactionRuleAction,
): string | undefined {
    if (action.actionType === RuleActionType.ACCRUAL_ACCOUNT_NAME) {
        return parseAccrualAccountActionValue(action.value).accountName;
    }
    return typeof action.value === "string" ? action.value : undefined;
}
